import { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/permissions";
import {
  SquaresFour, Robot, ChatsCircle, Plugs, GearSix,
  SignOut, User, ChartBar, Headset, UsersThree, Sparkle, ShieldCheck, Tray,
  CurrencyDollar, Lightbulb, Gauge, Tag, Phone, CaretDown, CaretRight,
} from "@phosphor-icons/react";

const primaryNav = [
  { to: "/app", label: "Översikt", icon: SquaresFour, end: true, testid: "nav-overview" },
  { to: "/app/agents", label: "Agenter", icon: Robot, testid: "nav-agents" },
  { to: "/app/telephony", label: "Telefoni", icon: Phone, testid: "nav-telephony", perm: "edit_agents" },
  { to: "/app/ai-insights", label: "AI-insights", icon: Sparkle, testid: "nav-ai-insights" },
  { to: "/app/analytics", label: "Analys", icon: ChartBar, testid: "nav-analytics" },
  { to: "/app/copilot", label: "AI Co-pilot", icon: Headset, testid: "nav-copilot", perm: "use_copilot" },
];

// Everything else lives under the "Inställningar" group.
const settingsNav = [
  { to: "/app/settings", label: "Allmänt", icon: GearSix, end: true, testid: "nav-settings" },
  { to: "/app/conversations", label: "Konversationer", icon: ChatsCircle, testid: "nav-conversations" },
  { to: "/app/knowledge-gaps", label: "Kunskapsluckor", icon: Lightbulb, testid: "nav-knowledge-gaps" },
  { to: "/app/quality", label: "Kvalitet", icon: Gauge, testid: "nav-quality" },
  { to: "/app/topics", label: "Ämnen", icon: Tag, testid: "nav-topics" },
  { to: "/app/costs", label: "Kostnader", icon: CurrencyDollar, testid: "nav-costs" },
  { to: "/app/leads", label: "Leads", icon: Tray, testid: "nav-leads", perm: "manage_integrations" },
  { to: "/app/team", label: "Team", icon: UsersThree, testid: "nav-team", perm: "manage_users" },
  { to: "/app/integrations", label: "Integrationer", icon: Plugs, testid: "nav-integrations", perm: "manage_integrations" },
  { to: "/app/compliance", label: "Efterlevnad", icon: ShieldCheck, testid: "nav-compliance", perm: "manage_compliance" },
];

export default function DashboardLayout() {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const primary = primaryNav.filter((i) => !i.perm || can(i.perm));
  const settings = settingsNav.filter((i) => !i.perm || can(i.perm));
  const inSettings = settings.some((i) => (i.end ? pathname === i.to : pathname.startsWith(i.to)));
  const [settingsOpen, setSettingsOpen] = useState(inSettings);
  const showSettings = settingsOpen || inSettings;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
      isActive ? "bg-[#0033FF] text-white shadow-sm" : "text-white/70 hover:bg-white/10 hover:text-white"
    }`;

  const subLinkCls = ({ isActive }) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
      isActive ? "bg-white/10 text-white" : "text-white/55 hover:bg-white/5 hover:text-white"
    }`;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-white/10 bg-zinc-950 sticky top-0 h-screen">
        <Link to="/app" className="flex items-center justify-center px-6 h-16 border-b border-white/10">
          <img src="/senzum-logo-white.png" alt="Senzum AI" className="h-8 w-auto" />
        </Link>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {primary.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid} className={linkCls}>
              <item.icon size={20} weight="bold" />
              {item.label}
            </NavLink>
          ))}

          {/* Settings group */}
          <button
            type="button"
            data-testid="nav-settings-group"
            onClick={() => setSettingsOpen((o) => !o)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
              inSettings ? "text-white bg-white/10" : "text-white/70 hover:bg-white/10 hover:text-white"
            }`}>
            <GearSix size={20} weight="bold" />
            Inställningar
            {showSettings ? <CaretDown size={14} weight="bold" className="ml-auto" /> : <CaretRight size={14} weight="bold" className="ml-auto" />}
          </button>
          {showSettings && (
            <div className="mt-1 ml-4 pl-3 border-l border-white/10 space-y-0.5" data-testid="settings-submenu">
              {settings.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid} className={subLinkCls}>
                  <item.icon size={16} weight="bold" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center text-white">
              <User size={18} weight="bold" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate text-white" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-xs text-white/50 truncate" data-testid="sidebar-user-role">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
          </div>
          <button data-testid="logout-button" onClick={handleLogout}
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white/70 hover:bg-white/10 hover:text-white transition-colors">
            <SignOut size={18} weight="bold" /> Logga ut
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* mobile top bar */}
        <div className="md:hidden flex items-center justify-between px-5 h-14 border-b border-zinc-200 bg-white sticky top-0 z-40">
          <Link to="/app" className="flex items-center">
            <img src="/senzum-logo.png" alt="Senzum AI" className="h-7 w-auto" />
          </Link>
          <button onClick={handleLogout} className="text-zinc-500"><SignOut size={20} weight="bold" /></button>
        </div>
        <main className="w-full max-w-[1600px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
